import React, { useEffect, useState, useCallback } from 'react';
import { CallingSystemHealth } from '../components/CallingSystemHealth';
import { useNavigate } from 'react-router-dom';
import { callAPI } from '../api';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { PhoneCall, Zap, Smartphone, Trash2 } from 'lucide-react';
import { formatDateTime } from '../lib/timeFormat';

const RANGES = [['week', 'Week'], ['month', 'Month'], ['year', 'Year'], ['custom', 'Custom']];

const Stat = ({ label, value, accent, testId }) => (
  <div className="clay-card p-4" data-testid={testId}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${accent || ''}`}>{value}</div>
  </div>
);
const money = (v) => (v == null ? '\u2014' : `\u20b9${Number(v).toLocaleString('en-IN')}`);

export const CallIntelligencePage = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filters, setFilters] = useState({ executive: '', lead_source: '', call_result: '', disposition: '' });
  const [data, setData] = useState(null);
  const [sla, setSla] = useState(null);
  const [devices, setDevices] = useState([]);
  const [tab, setTab] = useState('overview');
  const [access, setAccess] = useState(null);

  const buildParams = useCallback(() => {
    const p = range === 'custom' ? { date_from: from, date_to: to } : { period: range };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [range, from, to, filters]);

  const load = useCallback(async () => {
    try {
      const acc = await callAPI.ciAccess().then(r => r.data).catch(() => null);
      setAccess(acc);
      const [ci, s, dev] = await Promise.all([
        callAPI.intelligence(buildParams()),
        acc?.call_intelligence?.speed_to_lead
          ? callAPI.speedToLead(range === 'custom' ? { date_from: from, date_to: to } : { period: range }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
        callAPI.devices().catch(() => ({ data: [] })),
      ]);
      setData(ci.data); setSla(s.data); setDevices(dev.data || []);
    } catch (e) { /* silent */ }
  }, [buildParams, range, from, to]);
  useEffect(() => { load(); }, [load]);

  const opts = data?.filter_options || {};
  const resultChart = data ? Object.entries(data.kpis.by_result || {}).map(([k, v]) => ({ result: k, count: v })) : [];

  return (
    <div className="space-y-6" data-testid="call-intelligence-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10"><Zap className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="text-3xl font-outfit font-semibold">Call Intelligence</h1>
          <p className="text-muted-foreground">Team calling performance, speed-to-lead and attribution.</p>
        </div>
        {access?.call_intelligence?.label && (
          <span className="ml-auto rounded-full border border-primary/40 bg-primary/5 px-3 py-1 text-xs font-medium" data-testid="ci-scope-badge">
            Viewing: {access.call_intelligence.label}
          </span>
        )}
      </div>

      <CallingSystemHealth />

      <div className="flex gap-2 overflow-x-auto">
        {[['overview', 'Overview'],
          ...(access?.call_intelligence?.speed_to_lead === false ? [] : [['speed', 'Speed-to-Lead']]),
          ...(access?.call_intelligence?.source_performance === false ? [] : [['sources', 'Source Performance']]),
          ...(access?.calling_health?.scope === 'SELF' ? [] : [['devices', 'Devices']])].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ci-tab-${k}`}
            className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap shrink-0 ${tab === k ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>{l}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {RANGES.map(([k, l]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`ci-range-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs ${range === k ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>{l}</button>
        ))}
        {range === 'custom' && (
          <>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="ci-from" className="px-2 py-1 border rounded-lg text-xs bg-white dark:bg-slate-900" />
            <input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="ci-to" className="px-2 py-1 border rounded-lg text-xs bg-white dark:bg-slate-900" />
          </>
        )}
        {tab === 'overview' && (
          <>
            {access?.call_intelligence?.scope !== 'SELF' && (
            <select value={filters.executive} onChange={e => setFilters(f => ({ ...f, executive: e.target.value }))} data-testid="ci-filter-executive" className="px-2 py-1 border rounded-lg text-xs bg-white dark:bg-slate-900">
              <option value="">All Executives</option>
              {(opts.executives || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>)}
            <select value={filters.lead_source} onChange={e => setFilters(f => ({ ...f, lead_source: e.target.value }))} data-testid="ci-filter-source" className="px-2 py-1 border rounded-lg text-xs bg-white dark:bg-slate-900">
              <option value="">All Sources</option>
              {(opts.sources || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.call_result} onChange={e => setFilters(f => ({ ...f, call_result: e.target.value }))} data-testid="ci-filter-result" className="px-2 py-1 border rounded-lg text-xs bg-white dark:bg-slate-900">
              <option value="">All Results</option>
              {(opts.results || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </>
        )}
      </div>

      {tab === 'overview' && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Total Calls" value={data.kpis.attempted} testId="ci-total" />
            <Stat label="Connected" value={data.kpis.connected} accent="text-green-600" testId="ci-connected" />
            <Stat label="Connection Rate" value={`${data.kpis.connection_rate}%`} testId="ci-rate" />
            <Stat label="Total Talk" value={data.kpis.total_talk_hms} testId="ci-talk" />
            <Stat label="Avg Talk" value={data.kpis.avg_talk_hms} testId="ci-avgtalk" />
            <Stat label="No Answer" value={data.kpis.no_answer} accent="text-amber-600" testId="ci-noanswer" />
            <Stat label="Follow-ups" value={data.kpis.follow_ups_generated} testId="ci-fu" />
            <Stat label="Conversions" value={data.kpis.conversions_from_called} accent="text-green-600" testId="ci-conv" />
            <Stat label="Revenue (called)" value={money(data.kpis.revenue_from_called)} testId="ci-revenue" />
            <Stat label="Called Leads" value={data.kpis.called_leads} testId="ci-called" />
            <Stat label="Connected Leads" value={data.kpis.connected_leads} testId="ci-connleads" />
            <Stat label="Quotations" value={data.kpis.quotations_generated} testId="ci-quotes" />
          </div>

          {resultChart.length > 0 && (
            <div className="clay-card p-4">
              <h4 className="font-medium text-sm mb-2">Calls by Result</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={resultChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="result" fontSize={10} /><YAxis fontSize={10} />
                  <Tooltip /><Bar dataKey="count" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="clay-card p-4 overflow-x-auto" data-testid="ci-comparison">
            <h4 className="font-medium text-sm mb-3">Executive Comparison</h4>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground text-xs">
                <tr>{['Executive', 'Calls', 'Connected', 'Conn %', 'Talk Time', 'Avg Talk', 'Follow-ups', 'Conversions', 'Revenue'].map(h => <th key={h} className="py-2 pr-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.comparison.map(r => (
                  <tr key={r.user_id} className="border-t border-slate-100 dark:border-slate-800" data-testid="ci-comparison-row">
                    <td className="py-2 pr-3 font-medium">{r.name}</td>
                    <td>{r.calls}</td><td>{r.connected}</td><td>{r.connection_rate}%</td>
                    <td>{r.talk_time_hms}</td><td>{r.avg_talk_hms}</td><td>{r.follow_ups}</td>
                    <td className="text-green-600">{r.conversions}</td><td>{money(r.revenue)}</td>
                  </tr>
                ))}
                {data.comparison.length === 0 && <tr><td colSpan={9} className="py-4 text-center text-muted-foreground text-xs">No call data for this range/filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'speed' && sla && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Avg First Attempt" value={sla.leads_called ? sla.avg_first_attempt_hms : '—'} testId="sla-first-attempt" />
            <Stat label="Avg First Connect" value={sla.avg_first_connected_seconds ? sla.avg_first_connected_hms : '—'} testId="sla-first-connect" />
            <Stat label="Leads Called" value={`${sla.leads_called}/${sla.total_leads}`} testId="sla-called" />
            <Stat label={`Untouched > ${sla.sla_minutes}m`} value={sla.untouched_count} accent="text-red-600" testId="sla-untouched" />
          </div>
          <div className="clay-card p-4">
            <h4 className="font-medium text-sm mb-2">First-Call Response Time (SLA buckets)</h4>
            <div className="grid grid-cols-5 gap-2 text-center">
              {Object.entries(sla.buckets).map(([k, v]) => (
                <div key={k} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800" data-testid={`sla-bucket-${k}`}>
                  <div className="text-xs text-muted-foreground">{k}</div>
                  <div className="text-xl font-semibold">{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="clay-card p-4" data-testid="sla-untouched-list">
            <h4 className="font-medium text-sm mb-2">New Leads Awaiting First Call (SLA breached)</h4>
            {sla.untouched_over_sla.length === 0 ? <p className="text-xs text-muted-foreground">None — all new leads contacted on time.</p> : (
              <div className="space-y-1">
                {sla.untouched_over_sla.slice(0, 20).map(u => (
                  <button key={u.lead_id} onClick={() => navigate(`/leads/${u.lead_id}`)} className="w-full text-left text-sm p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 flex justify-between">
                    <span>{u.client} · {u.lead_source || '—'}</span>
                    <span className="text-red-600 text-xs">{u.minutes_waiting}m waiting</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'sources' && data && (
        <div className="clay-card p-4 overflow-x-auto" data-testid="ci-source-table">
          <h4 className="font-medium text-sm mb-3">Source + Call Performance</h4>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground text-xs">
              <tr>{['Source', 'Leads', 'Calls', 'Connected', 'Conn %', 'Avg Talk', 'Converted', 'Revenue'].map(h => <th key={h} className="py-2 pr-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.by_source.map(r => (
                <tr key={r.source} className="border-t border-slate-100 dark:border-slate-800" data-testid="ci-source-row">
                  <td className="py-2 pr-3 font-medium">{r.source}</td>
                  <td>{r.leads}</td><td>{r.calls}</td><td>{r.connected}</td><td>{r.connection_rate}%</td>
                  <td>{r.avg_talk_hms}</td><td className="text-green-600">{r.converted}</td><td>{money(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'devices' && (
        <DeviceManager devices={devices} opts={opts} reload={load} />
      )}
    </div>
  );
};

const DeviceManager = ({ devices, opts, reload }) => {
  const [name, setName] = useState('');
  const [assignee, setAssignee] = useState('');
  const [issued, setIssued] = useState(null);
  const register = async () => {
    if (!name || !assignee) { toast.error('Device name and assignee required'); return; }
    try {
      const r = await callAPI.registerDevice({ device_name: name, assigned_user_id: assignee });
      setIssued(r.data); setName(''); setAssignee(''); reload();
      toast.success('Device registered');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-4" data-testid="device-manager">
      <div className="clay-card p-4">
        <h4 className="font-medium text-sm mb-1 flex items-center gap-2"><Smartphone className="h-4 w-4" /> Register GNONS Android Device</h4>
        <p className="text-xs text-muted-foreground mb-3">Issues a scoped device token for the future Android calling app. The token maps all its call events to the assigned executive.</p>
        <div className="flex flex-wrap gap-2">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Device name" data-testid="device-name" className="px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900" />
          <select value={assignee} onChange={e => setAssignee(e.target.value)} data-testid="device-assignee" className="px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900">
            <option value="">Assign to…</option>
            {(opts.executives || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <button onClick={register} data-testid="device-register" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Register</button>
        </div>
        {issued && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-xs" data-testid="device-token-issued">
            <b>Device token (store securely in the app):</b>
            <code className="block mt-1 break-all">{issued.device_token}</code>
          </div>
        )}
      </div>
      <div className="clay-card p-4 overflow-x-auto">
        <h4 className="font-medium text-sm mb-3">Registered Devices</h4>
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground text-xs">
            <tr>{['Device', 'Assigned User', 'Status', 'App Version', 'Last Sync', 'Last Event', ''].map(h => <th key={h} className="py-2 pr-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {devices.map(d => (
              <tr key={d.id} className="border-t border-slate-100 dark:border-slate-800" data-testid="device-row">
                <td className="py-2 pr-3">{d.device_name}</td><td>{d.assigned_user_name}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full ${d.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{d.status}</span></td>
                <td>{d.app_version || '—'}</td><td className="text-xs">{d.last_sync ? formatDateTime(d.last_sync) : 'never'}</td>
                <td className="text-xs">{d.last_call_event || '—'}</td>
                <td>{d.status === 'active' && <button onClick={async () => { await callAPI.revokeDevice(d.id); reload(); }} data-testid="device-revoke" className="text-red-600"><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
            {devices.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-muted-foreground text-xs">No devices registered yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default CallIntelligencePage;
